/**
 * Controle de ajuste manual de LR na mesa (E33.F2 / ADR-0044). // ux-neutrality:allow doc-comment
 *
 * Input numérico do LR EFETIVO (o valor que o perito assina), estilizado como // ux-neutrality:allow doc-comment
 * o texto do LR (sem moldura até foco/hover) para não poluir a linha. Ao // ux-neutrality:allow doc-comment
 * commitar (Enter ou blur) com um valor novo e válido, chama `onCommit`; o pai // ux-neutrality:allow doc-comment
 * registra o override e recarrega o workspace, então o `effective` chega de // ux-neutrality:allow doc-comment
 * volta como fonte única. Quando há override, exibe o selo "Ajustado" + o valor // ux-neutrality:allow doc-comment
 * calculado pelo motor e um botão de reverter. // ux-neutrality:allow doc-comment
 *
 * Reusado pelo CriteriaPanel (por critério) e pelo LRMeter (LR total). // ux-neutrality:allow doc-comment
 */
import { useEffect, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { lrToSupportIndex, SUPPORT_INDEX_MAX, SUPPORT_INDEX_MIN, supportIndexToLr } from '@/lib/lr';
import { LR_MAX, LR_MIN } from '@/lib/lr_adjustment';

/**
 * Formata o LR com 3 algarismos significativos (mesma régua do texto read-only // ux-neutrality:allow doc-comment
 * `lr.toPrecision(3)`), sem notação científica nem dízima longa na faixa usual. // ux-neutrality:allow doc-comment
 */
function formatLr(value: number): string {
  return Number(value.toPrecision(3)).toString();
}

/** Modo de edição: o LR efetivo (critério) ou o índice de bancada (composto). */
export type LrAdjustKind = 'lr' | 'index';

export interface LrAdjustControlProps {
  /** LR efetivo atual (o que o perito vê e assina). */
  effective: number;
  /** Há override manual neste LR (mostra o selo "Ajustado" + reverter). */
  adjusted: boolean;
  /** Desabilita os controles enquanto outro ajuste está em voo. */
  busy?: boolean;
  /** Prefixo dos `data-testid` (ex.: `criterion-calibre` | `composite`). */
  testIdPrefix: string;
  /** Rótulo acessível do input (ex.: "Ajustar o valor de LR — Calibre"). */
  inputAriaLabel: string;
  /**
   * E39.F1/ADR-0054: `'index'` edita a régua de BANCADA assinada (−100..+100); // ux-neutrality:allow doc-comment
   * o valor digitado é convertido para LR (`10^(índice/50)`) antes de `onCommit`, // ux-neutrality:allow doc-comment
   * então a trilha do ADR-0044 segue em LR. Default `'lr'` (critérios). // ux-neutrality:allow doc-comment
   */
  kind?: LrAdjustKind;
  onCommit: (value: number) => void | Promise<void>;
  onClear: () => void | Promise<void>;
}

export function LrAdjustControl({
  effective,
  adjusted,
  busy = false,
  testIdPrefix,
  inputAriaLabel,
  kind = 'lr',
  onCommit,
  onClear,
}: LrAdjustControlProps): JSX.Element {
  const isIndex = kind === 'index';
  // Valor exibido na unidade do modo: LR efetivo, ou o índice de bancada
  // (espelho do backend, com banda morta e saturação) derivado do LR efetivo.
  const displayValue = isIndex ? Math.round(lrToSupportIndex(effective) ?? 0) : effective;
  const minBound = isIndex ? SUPPORT_INDEX_MIN : LR_MIN;
  const maxBound = isIndex ? SUPPORT_INDEX_MAX : LR_MAX;
  const format = (value: number): string => (isIndex ? String(Math.round(value)) : formatLr(value));

  const [draft, setDraft] = useState(() => format(displayValue));
  const [focused, setFocused] = useState(false);

  // Enquanto o input não está em foco, espelha o valor efetivo (atualizado pelo
  // refetch após cada ajuste). Em foco, preserva o que o perito está digitando.
  useEffect(() => {
    if (!focused) setDraft(format(displayValue));
    // `format`/`displayValue` derivam de `effective`+`kind` (estáveis no render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effective, focused, kind]);

  function commit(): void {
    const raw = draft.trim();
    // Texto inalterado vs. o exibido → no-op. Compara contra a forma FORMATADA
    // (não o cru): focar/desfocar sem editar não pode criar override espúrio por
    // diferença de arredondamento (3 algarismos no LR, inteiro no índice).
    if (raw === format(displayValue)) return;
    const parsed = raw === '' ? Number.NaN : Number(raw);
    // Não-numérico → restaura o exibido, sem PATCH.
    if (!Number.isFinite(parsed)) {
      setDraft(format(displayValue));
      return;
    }
    if (isIndex) {
      // Régua de bancada SATURA no teto (ADR-0054): fora de [−100,+100] grava o
      // limite, não rejeita (ex.: 9000 → +100 ⇔ LR=100; −9000 → −100 ⇔ LR=1/100).
      const clamped = Math.max(minBound, Math.min(maxBound, Math.round(parsed)));
      setDraft(format(clamped));
      if (clamped === displayValue) return; // já é o valor exibido → no-op
      // Índice → LR pelo inverso bijetivo (ADR-0044 persiste o LR resultante).
      void onCommit(supportIndexToLr(clamped));
      return;
    }
    // Modo LR: valor fora da faixa de sanidade → restaura o exibido, sem PATCH.
    if (parsed < minBound || parsed > maxBound) {
      setDraft(format(displayValue));
      return;
    }
    void onCommit(parsed);
  }

  return (
    // <div> (não <form>): o controle é montado dentro de um <p> (LRMeter) e de
    // uma linha de critério — <form> aninhado seria HTML inválido. Enter commita
    // via onKeyDown; blur também commita.
    <span className="lr-adjust" data-testid={`${testIdPrefix}-lr-adjust`}>
      {!isIndex && (
        <span className="lr-adjust-prefix" aria-hidden="true">
          LR =
        </span>
      )}
      <input
        type="number"
        inputMode={isIndex ? 'numeric' : 'decimal'}
        step={isIndex ? 1 : 'any'}
        min={minBound}
        max={maxBound}
        className={isIndex ? 'lr-adjust-input lr-adjust-input--index' : 'lr-adjust-input'}
        value={draft}
        disabled={busy}
        aria-label={inputAriaLabel}
        data-testid={`${testIdPrefix}-lr-input`}
        onChange={(event) => setDraft(event.target.value)}
        onFocus={() => setFocused(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.currentTarget.blur();
          }
        }}
        onBlur={() => {
          setFocused(false);
          commit();
        }}
      />
      {/* Quando há override, basta a seta de reverter — a própria presença dela
          sinaliza "alterado". Sem selo "Ajustado" para caber em uma linha. */}
      {adjusted && (
        <button
          type="button"
          className="lr-adjust-revert"
          disabled={busy}
          aria-label={ptBR.workspaceCaso.lrAdjustRevert}
          title={ptBR.workspaceCaso.lrAdjustRevert}
          data-testid={`${testIdPrefix}-lr-revert`}
          onClick={() => void onClear()}
        >
          ↺
        </button>
      )}
    </span>
  );
}
